import { FC, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigationType } from "react-router-dom";

import { LayoutProvider, useLayout } from "./use-layout";
export interface BasePageLayoutProps {
  renderSidebar: () => React.ReactNode;
  renderContent: () => React.ReactNode;
  offsetTop?: number | null | undefined;
}
const Inner: FC<BasePageLayoutProps> = ({ renderContent, renderSidebar }) => {
  const { t } = useTranslation("common");
  const { offsetTop, hasSidebar } = useLayout();
  const mainRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const navigationType = useNavigationType();
  const isInitialRenderRef = useRef(true);

  // Focus main only on user-initiated navigation (PUSH). Initial load and the
  // REPLACE redirects that follow it (e.g. list-view defaults) must not steal
  // focus, or the skip link becomes unreachable by forward tabbing.
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    if (navigationType !== "PUSH") {
      return;
    }
    mainRef.current?.focus({ preventScroll: true });
  }, [location.pathname, navigationType]);

  return (
    <div className="flex h-full min-h-screen w-full flex-row overflow-hidden bg-canvas text-foreground">
      {hasSidebar && renderSidebar()}

      <main
        ref={mainRef}
        tabIndex={-1}
        id="main-content"
        aria-label={t("a11y.mainContent")}
        className="relative m-0 flex min-w-0 shrink grow basis-0 flex-col bg-background outline-hidden"
        style={{
          maxHeight: `calc(100vh - ${offsetTop}px)`,
          marginTop: `${offsetTop}px`,
        }}
      >
        {renderContent()}
      </main>
    </div>
  );
};

export const BasePageLayout: FC<BasePageLayoutProps> = ({ renderContent, renderSidebar, offsetTop }) => {
  return (
    <LayoutProvider offsetTop={offsetTop ?? 0}>
      <Inner renderContent={renderContent} renderSidebar={renderSidebar} />
    </LayoutProvider>
  );
};
